/**
 * Simple secure JSON parsing utility
 * Prevents security issues from malformed JSON
 */

export interface ValidationResult<T = any> {
  success: boolean;
  data?: T;
  errors?: string[];
}

/**
 * Safely parse JSON string - prevents security issues from malformed JSON
 * @param jsonString - The JSON string to parse
 * @returns ValidationResult with parsed data or errors
 */
export function safeJsonParse<T = any>(
  jsonString: string
): ValidationResult<T> {
  try {
    const parsedData = JSON.parse(jsonString) as T;
    return {
      success: true,
      data: parsedData,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown parsing error";
    return {
      success: false,
      errors: [`Invalid JSON: ${errorMessage}`],
    };
  }
}

/**
 * Safely parse JSON string and validate against a JSON Schema
 * @param jsonString - The JSON string to parse
 * @param schema - JSON Schema object to validate against
 * @returns ValidationResult with parsed and validated data or errors
 */
export async function safeJsonParseWithSchema<T>(
  jsonString: string,
  schema: object
): Promise<ValidationResult<T>> {
  // First, parse the JSON
  const parseResult = safeJsonParse(jsonString);
  if (!parseResult.success) {
    return parseResult;
  }

  // Then validate against the schema
  try {
    const { default: Ajv } = await import("ajv");

    const ajv = new Ajv();
    const validate = ajv.compile(schema);

    if (validate(parseResult.data)) {
      return {
        success: true,
        data: parseResult.data as T,
      };
    } else {
      return {
        success: false,
        errors: validate.errors?.map((err) => {
          const path = (err as any).instancePath || (err as any).dataPath || "";
          const pathPrefix = path ? `${path}: ` : "";
          return `${pathPrefix}${err.message}`;
        }) || ["Schema validation failed"],
      };
    }
  } catch (error) {
    return {
      success: false,
      errors: ["Schema validation failed"],
    };
  }
}
